import { useContext, useRef } from 'react'
import {
  useInteractionOutside,
  useSearchEstablishmentsList
} from '@/modules/accredited-establishments/hooks'
import { FiltersContext } from '@/modules/accredited-establishments/contexts'
import {
  makeFiltersArgs,
  getEstablishmentDefaultImage,
  formatAddressLines,
  getEstablishmentTags
} from '@/modules/accredited-establishments/functions'
import {
  useDrawer,
  useIntersectionObserver,
  useMediaQuery
} from '@/global/hooks'
import { WParagraph, WIcon, WButton } from '@alice-health/wonderland-react'
import {
  CLUSTER_ITEMS_LIST_ID,
  CLUSTER_ITEMS_LIST_PANEL_ID,
  REACH_END_MESSAGE_THRESHOLD
} from './ClusterItemsListPanel.helpers'
import {
  EstablishmentCategory,
  EstablishmentsWithLatLonArgs
} from '@/modules/accredited-establishments/api/types'
import { SlidePanel } from '@/global/components/SlidePanel'
import {
  DEFAULT_LON,
  DEFAULT_LAT
} from '@/modules/accredited-establishments/constants'
import {
  EstablishmentsListItem,
  ScrollToTopMessage,
  OneColumnListItemSkeleton,
  ESTABLISHMENT_DRAWER_ID,
  SPECIALIST_DRAWER_ID
} from '@/modules/accredited-establishments/components'
import classNames from 'classnames'
import { zIndex } from '@/global/constants/z-index'

interface ClusterItemsListPanelProps {
  onEstablishmentClick?: ({
    referencedId,
    category
  }: {
    referencedId: string
    category: EstablishmentCategory
  }) => void
}

const ClusterItemsListPanel = ({
  onEstablishmentClick
}: ClusterItemsListPanelProps) => {
  const ref = useRef<HTMLDivElement>(null)

  const isMobile = useMediaQuery({ maxWidth: 'mobile' })

  const { close: closeClusterItemsListPanel } = useDrawer(
    CLUSTER_ITEMS_LIST_PANEL_ID
  )
  const { isOpen: isSpecialistDrawerOpen } = useDrawer(SPECIALIST_DRAWER_ID)
  const { isOpen: isEstablishmentDrawerOpen } = useDrawer(
    ESTABLISHMENT_DRAWER_ID
  )
  const {
    clusterLocation,
    categories,
    plan,
    specialty,
    gender,
    appointmentType,
    providerName
  } = useContext(FiltersContext)

  useInteractionOutside({
    ref,
    callback: () => {
      if (!isSpecialistDrawerOpen && !isEstablishmentDrawerOpen) {
        closeClusterItemsListPanel()
      }
    }
  })

  const filterArgs = makeFiltersArgs({
    categories,
    plan,
    specialty,
    gender,
    appointmentType,
    providerName
  })

  const searchArgs: EstablishmentsWithLatLonArgs = {
    ...filterArgs,
    lat: clusterLocation ? clusterLocation.lat : DEFAULT_LAT,
    lon: clusterLocation ? clusterLocation.lon : DEFAULT_LON,
    radiusInMeters: clusterLocation?.radiusInMeters.toString()
  }

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useSearchEstablishmentsList({ ...searchArgs, enabled: !!clusterLocation })

  const { observeElementRef } = useIntersectionObserver<HTMLButtonElement>({
    callback: isIntersecting => {
      if (isIntersecting && hasNextPage) fetchNextPage()
    }
  })

  const items = data?.pages.map(page => page.items).flat() || []
  const hasItems = !isLoading && items.length
  const reachedEnd =
    !isLoading &&
    !isFetchingNextPage &&
    !hasNextPage &&
    items.length > REACH_END_MESSAGE_THRESHOLD

  return (
    <SlidePanel
      id={CLUSTER_ITEMS_LIST_PANEL_ID}
      ref={ref}
      className={classNames(
        'lg:top-[var(--app-header-height)]',
        !isMobile && zIndex.accreditedEstablishments.desktopClusterListPanel
      )}
      position={isMobile ? 'bottom' : 'left'}
      backdrop={isMobile ? true : false}
      closeButton={false}
    >
      <div className='w-full lg:w-[495px] h-[calc(100vh-100vh*0.2)] lg:h-[calc(100vh-var(--app-header-height))] flex flex-col justify-between lg:flex-row'>
        {/*
         * O cartao passou a ter borda e cantos arredondados proprios (HC-2612).
         * Empilhado sem espaco, as bordas de dois cartoes ficam coladas, entao
         * a coluna precisa de respiro e de recuo lateral.
         */}
        <div
          className='flex flex-col gap-gl-spacing-02 p-gl-spacing-04 overflow-y-auto'
          id={CLUSTER_ITEMS_LIST_ID}
        >
          {hasItems
            ? items.map(item => (
                /*
                 * O cartao usa `h-full` para igualar as alturas da grade da
                 * listagem. Aqui a coluna tem altura definida, e sem esta caixa
                 * de altura automatica cada cartao esticaria ate o fim do
                 * painel.
                 */
                <div
                  className='shrink-0'
                  key={item.referencedId}
                >
                  <EstablishmentsListItem
                    ref={observeElementRef}
                    referencedId={item.referencedId}
                    title={item.name}
                    onClick={() =>
                      onEstablishmentClick?.({
                        referencedId: item.referencedId,
                        category: item.category
                      })
                    }
                    address={formatAddressLines(item.address)}
                    image={
                      item.icon || getEstablishmentDefaultImage(item.category)
                    }
                    tags={getEstablishmentTags(item, specialty)}
                  />
                </div>
              ))
            : null}

          {isFetchingNextPage ? (
            <div className='mx-auto my-gl-spacing-24 w-[190px] flex flex-col justify-center items-center gap-gl-spacing-06 '>
              <WIcon
                className='text-gl-color-shades-magenta-60 animate-spin'
                icon='icLoad'
                size='xlarge'
              />
              <WParagraph
                className='text-center'
                variant='heavy'
                size='large'
              >
                Carregando mais instituições para você...
              </WParagraph>
            </div>
          ) : null}

          {reachedEnd ? <ScrollToTopMessage /> : null}

          {isLoading ? <OneColumnListItemSkeleton /> : null}
        </div>

        <div
          className='group hover:bg-gl-color-shades-gray-10 hover:cursor-pointer border-t-sys-color-stroke-active border-t-gl-border-width-xs lg:border-x-sys-color-stroke-active lg:border-x-gl-border-width-xs p-gl-spacing-03 flex justify-center items-center'
          onClick={closeClusterItemsListPanel}
        >
          <WButton
            className='group-hover:bg-gl-color-shades-gray-10 w-fit h-fit'
            variant='tertiary'
            iconButton
            icon='icClose'
            size='medium'
            onClick={closeClusterItemsListPanel}
          />
        </div>
      </div>
    </SlidePanel>
  )
}

export { ClusterItemsListPanel }
export type { ClusterItemsListPanelProps }
